import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { useVisualizationImage } from '../../modules/documents/useVisualizationImage';
import { MarkdownContent } from './MarkdownContent';

/** Full-size viewer for a single visualization, with zoom/fit controls and
 * optional related Markdown content (e.g. the section it belongs to) shown below. */
export function VisualizationViewerDialog({
  viz,
  title,
  subtitle,
  markdownContent,
  onClose,
}: {
  viz: { id: number } | null;
  title?: string;
  subtitle?: string | null;
  markdownContent?: string | null;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (viz) setZoom(1);
  }, [viz]);

  const { src, isLoading } = useVisualizationImage(viz?.id, viz != null);

  const handleZoomIn = () => setZoom((z) => Math.min(+(z + 0.25).toFixed(2), 4));
  const handleZoomOut = () => setZoom((z) => Math.max(+(z - 0.25).toFixed(2), 0.25));
  const handleZoomReset = () => setZoom(1);

  return (
    <Dialog open={viz !== null} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { minHeight: '80vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        {title ?? 'Visualization'}
        {subtitle ? ` — ${subtitle}` : ''}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
              <Tooltip title="Zoom in">
                <span>
                  <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 4}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Zoom out">
                <span>
                  <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 0.25}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Fit to window">
                <span>
                  <IconButton size="small" onClick={handleZoomReset} disabled={zoom === 1}>
                    <ZoomOutMapIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {Math.round(zoom * 100)}%
              </Typography>
            </Stack>

            <Box
              sx={{
                overflow: 'auto',
                display: 'flex',
                alignItems: zoom <= 1 ? 'center' : 'flex-start',
                justifyContent: zoom <= 1 ? 'center' : 'flex-start',
                minHeight: 420,
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1,
              }}
            >
              {isLoading || !src ? (
                <CircularProgress />
              ) : (
                <Box
                  component="img"
                  src={src}
                  alt={title ?? 'Visualization'}
                  sx={{
                    display: 'block',
                    width: zoom !== 1 ? `${zoom * 100}%` : 'auto',
                    maxWidth: zoom === 1 ? '100%' : 'none',
                    maxHeight: zoom === 1 ? 520 : 'none',
                    objectFit: 'contain',
                    transition: 'width 0.15s, max-width 0.15s',
                  }}
                />
              )}
            </Box>
          </Box>

          {markdownContent && (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Section Content
              </Typography>
              <Divider sx={{ mt: 0.5, mb: 1 }} />
              <MarkdownContent sx={{ fontSize: '0.875rem', maxHeight: 220, overflowY: 'auto' }}>
                {markdownContent}
              </MarkdownContent>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
